import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../auth/AuthContext';
import { createProject, updateProject } from '../data/projects';
import { streetViewThumb } from '../data/locate';
import { listStakeholders, getStakeholder } from '../data/stakeholders';
import PropertyLocator from './PropertyLocator';
import { uploadFile } from '../data/upload';
import Roadmap, { reachedStageFor } from '../components/Roadmap';
import AgentActivity from '../components/AgentActivity';
import DocumentArchive from '../components/DocumentArchive';
import StakeholderModal from './StakeholderModal';
import ConfirmDialog from '../components/ConfirmDialog';
import './NewProject.css';

// Roles a stakeholder can hold in a project (spec 4.2 / 6.2).
const ROLE_OPTIONS = [
  'מזמין העבודה / יזם',
  'מנהל פרויקט',
  'אדריכל',
  'עורך הניתוח',
  'יועץ',
  'חברה מנהלת',
  'נציג דיירים',
  'אחר',
];

const WORK_ORDERER_ROLE = 'מזמין העבודה / יזם';

// Check-type chips shown under the brief (spec 4.1) — grouped for readability, but
// stored as one flat list. Selecting them gives the analysis early context.
const CHECK_CHIP_GROUPS = [
  ['הריסה ובנייה', 'חיזוק ותוספות', 'איחוד וחלוקה', 'שימור'],
  ['מגרש ריק', 'בניין קיים', 'תוספת קומות', 'תוספת ממ״דים'],
  ['שינוי ייעוד', 'תוספת מרפסות', 'בדיקת חניה', 'בדיקת תת־קרקע', 'מרתפים'],
  ['התחדשות עירונית', 'פינוי־בינוי', 'תמ״א', 'חלופת תמ״א', 'בדיקת זכויות כללית'],
];

function parseCheckTags(raw) {
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string' && raw.trim()) {
    try { const v = JSON.parse(raw); return Array.isArray(v) ? v : []; } catch { return []; }
  }
  return [];
}

// Accepted document kinds + 50MB cap, mirrored in the drop-zone hint.
const DOC_ACCEPT = '.pdf,.dwg,.dwf,.jpg,.jpeg,.png';
const DOC_ALLOWED_EXT = ['pdf', 'dwg', 'dwf', 'jpg', 'jpeg', 'png'];
const DOC_MAX_BYTES = 50 * 1024 * 1024;

function fileExt(name) {
  const m = /\.([a-z0-9]+)$/i.exec(name || '');
  return m ? m[1].toLowerCase() : '';
}
function fileTypeLabel(name) {
  const e = fileExt(name);
  return e === 'jpeg' ? 'JPG' : e.toUpperCase();
}
function formatBytes(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(n) / Math.log(1024)));
  const val = n / 1024 ** i;
  return `${val >= 10 || i === 0 ? Math.round(val) : val.toFixed(1)} ${units[i]}`;
}
function docId() {
  return (window.crypto && window.crypto.randomUUID)
    ? window.crypto.randomUUID()
    : `doc-${Math.round(window.performance.now())}-${Math.floor(Math.random() * 1e6)}`;
}

// Build the form's initial state — blank for a new project, or prefilled from a
// loaded project ({project, property, brief, stakeholders, documents}) for edit.
function initialState(loaded, defaultEditor = '') {
  const p = loaded?.project;
  const prop = loaded?.property || {};
  if (!p) {
    // A fresh project starts empty. The pilot is Tel Aviv-Yafo only, so the city
    // defaults; one blank work-orderer row guides the stakeholders section.
    return {
      name: '', editor: defaultEditor, locMode: 'address',
      city: 'תל אביב-יפו', street: '', houseNo: '',
      gush: '', helka: '', subHelka: '', brief: '', checkTags: [],
      stakeholders: [{ id: 'wo-0', role: WORK_ORDERER_ROLE, company: '', contact: '', phone: '', email: '' }],
      docs: [],
    };
  }
  return {
    name: p.name || '',
    editor: p.analysis_editor || defaultEditor,
    locMode: prop.loc_mode || 'address',
    city: prop.city || '',
    street: prop.street || '',
    houseNo: prop.house_number || '',
    gush: prop.block || '',
    helka: prop.parcel || '',
    subHelka: prop.sub_parcel || '',
    brief: loaded?.brief?.brief_text || '',
    checkTags: parseCheckTags(loaded?.brief?.check_tags),
    stakeholders: (loaded?.stakeholders || []).map((s) => ({
      id: s.id, role: s.role_in_project, company: s.company,
      contact: s.contact, phone: s.phone, email: s.email,
    })),
    docs: (loaded?.documents || []).map((d) => ({
      id: d.id, name: d.file_name || d.document_type, size: d.file_size, type: d.document_type,
    })),
  };
}

const BRIEF_PLACEHOLDER =
  'תארו את מטרת הבדיקה והפרוגרמה — לדוגמה: בדיקת המצב הסטטוטורי של הבניין ' +
  'ברחוב שלום עליכם 38, תל אביב-יפו, לצורך בחינת פוטנציאל לחיזוק ותוספת ' +
  'יחידות דיור במסגרת תמ"א 38 / התחדשות עירונית.';

function Section({ icon, title, helper, action, children }) {
  return (
    <section className="np-section">
      <div className="np-section-label">
        <span className="np-section-icon"><i className={`fi ${icon}`} /></span>
        <div className="np-section-titles">
          <h3>{title}</h3>
          {helper && <p>{helper}</p>}
        </div>
        {action}
      </div>
      <div className="np-section-body">{children}</div>
    </section>
  );
}

function Field({ label, required, children, className = '' }) {
  return (
    <label className={`np-field ${className}`}>
      <span className="np-field-label">{label}{required && <i className="np-req">*</i>}</span>
      {children}
    </label>
  );
}

export default function NewProject({ project, onCancel, onSaved, onNext, onGoStage }) {
  const { user } = useAuth();
  const editId = project?.project?.id || null;
  const isEdit = Boolean(editId);
  const init = React.useMemo(() => initialState(project, user?.name || ''), [project, user]);

  // Once a brand-new project is first saved it gets an id; keep it so subsequent
  // saves UPDATE that same project instead of creating duplicates (spec 3.1).
  const [liveId, setLiveId] = useState(editId);
  const savedOnce = Boolean(liveId);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveNotice, setSaveNotice] = useState('');
  const [locatedLatLng, setLocatedLatLng] = useState(null); // for the Street View thumbnail
  const [name, setName] = useState(init.name);
  const [editor, setEditor] = useState(init.editor);
  const [locMode, setLocMode] = useState(init.locMode); // 'address' | 'parcel'
  const [city, setCity] = useState(init.city);
  const [street, setStreet] = useState(init.street);
  const [houseNo, setHouseNo] = useState(init.houseNo);
  const [gush, setGush] = useState(init.gush);
  const [helka, setHelka] = useState(init.helka);
  const [subHelka, setSubHelka] = useState(init.subHelka);
  const [brief, setBrief] = useState(init.brief);
  const [checkTags, setCheckTags] = useState(init.checkTags);
  const [stakeholders, setStakeholders] = useState(init.stakeholders);
  const [docs, setDocs] = useState(init.docs);

  // "Dirty" check — lets Next skip the save (a no-op) when nothing changed.
  const serializeForm = (st) => JSON.stringify({
    name: (st.name || '').trim(), editor: st.editor || '', locMode: st.locMode,
    city: st.city || '', street: st.street || '', houseNo: st.houseNo || '',
    gush: st.gush || '', helka: st.helka || '', subHelka: st.subHelka || '', brief: st.brief || '',
    checkTags: [...(st.checkTags || [])].sort(),
    stakeholders: (st.stakeholders || []).map((s) => ({ role: s.role, company: s.company || '', contact: s.contact || '', phone: s.phone || '', email: s.email || '', stakeholderId: s.stakeholderId || null })),
    docs: (st.docs || []).map((d) => ({ name: d.name, type: d.type, size: d.size })),
  });
  const initialSig = React.useMemo(() => serializeForm(init), [init]);
  // Baseline the "dirty" check tracks — reset after every successful save so a
  // subsequent Next with no further edits is a true no-op (no redundant save).
  const [baselineSig, setBaselineSig] = useState(initialSig);
  useEffect(() => { setBaselineSig(initialSig); }, [initialSig]);
  const currentSig = () => serializeForm({ name, editor, locMode, city, street, houseNo, gush, helka, subHelka, brief, checkTags, stakeholders, docs });
  const isDirty = () => currentSig() !== baselineSig;

  const removeStakeholder = (id) => setStakeholders((rows) => rows.filter((r) => r.id !== id));
  const setStakeholderField = (id, field, value) =>
    setStakeholders((rows) => rows.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
  const removeDoc = (id) => setDocs((rows) => rows.filter((r) => r.id !== id));
  const toggleTag = (t) => setCheckTags((arr) => (arr.includes(t) ? arr.filter((x) => x !== t) : [...arr, t]));

  // Full create/edit of a directory stakeholder from the project page (spec 1.2/1.3).
  // shModal = null | { rowId, stakeholder, seed } — rowId links the result to a row.
  const [shModal, setShModal] = useState(null);
  const rowSeed = (row) => ({ name: row.company, role: row.role, email: row.email, phone: row.phone, contact: row.contact });
  const rowFromStakeholder = (r) => {
    const cs = r.contacts || [];
    const primary = cs.find((c) => c.is_primary ?? c.isPrimary) || cs[0] || {};
    return {
      stakeholderId: r.id, role: r.role || ROLE_OPTIONS[0], company: r.name || '',
      contact: primary.name || '', phone: r.phone || primary.phone || '', email: r.email || primary.email || '',
    };
  };
  const openNewStakeholder = () => setShModal({ rowId: null, stakeholder: null, seed: null });
  const openEditStakeholder = async (row) => {
    if (row.stakeholderId) {
      try { setShModal({ rowId: row.id, stakeholder: await getStakeholder(row.stakeholderId) }); return; }
      catch { /* fall back to seeding from the row */ }
    }
    setShModal({ rowId: row.id, stakeholder: null, seed: rowSeed(row) });
  };
  const onStakeholderSaved = (result) => {
    const patch = rowFromStakeholder(result);
    setStakeholders((rows) => {
      if (shModal?.rowId) return rows.map((r) => (r.id === shModal.rowId ? { ...r, ...patch } : r));
      return [...rows, { id: `dir-${result.id}-${rows.length}`, ...patch }];
    });
    setShModal(null);
    setDirectory([]); // force the picker to reload with the new/updated entry
  };

  // Documents — metadata-only for now (names/sizes/types persist; bytes later).
  const fileInputRef = useRef(null);
  const [docError, setDocError] = useState('');
  const [dragOver, setDragOver] = useState(false);

  // Live upload feedback (graphic-log §1.2): every file gets a visible row with
  // a progress bar while uploading, then flips to success or an explicit error —
  // an upload must never look identical to a stall.
  const [uploads, setUploads] = useState([]);
  const patchUpload = (id, patch) => setUploads((rows) => rows.map((u) => (u.id === id ? { ...u, ...patch } : u)));

  const addFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const rejected = [];
    const ok = [];
    files.forEach((f) => {
      if (!DOC_ALLOWED_EXT.includes(fileExt(f.name))) { rejected.push(`${f.name} — סוג קובץ לא נתמך`); return; }
      if (f.size > DOC_MAX_BYTES) { rejected.push(`${f.name} — חורג מ-50MB`); return; }
      ok.push(f);
    });
    setDocError(rejected.length ? `לא נוספו: ${rejected.join('; ')}` : '');
    // Upload each accepted file to Vercel Blob (store the URL); the row appears
    // once uploaded so the archive can open it. Falls back to metadata-only.
    for (const f of ok) {
      const upId = docId();
      setUploads((rows) => [...rows, { id: upId, name: f.name, pct: 0, status: 'uploading' }]);
      let fileId = null;
      try {
        fileId = await uploadFile(f, { onProgress: (pct) => patchUpload(upId, { pct: Math.round(pct) }) });
        patchUpload(upId, { pct: 100, status: 'done' });
        setTimeout(() => setUploads((rows) => rows.filter((u) => u.id !== upId)), 2500);
      } catch {
        // Keep the row as an error (dismissed manually); the doc row below still
        // records the metadata so the file isn't silently lost from the list.
        patchUpload(upId, { status: 'error' });
      }
      setDocs((rows) => [...rows, { id: docId(), name: f.name, size: formatBytes(f.size), type: fileTypeLabel(f.name), fileId }]);
    }
  };

  const onDropFiles = (e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); };
  const openFilePicker = () => fileInputRef.current?.click();

  // Pick a stakeholder from the global directory (reuse across projects).
  const [directory, setDirectory] = useState([]);
  const [dirError, setDirError] = useState('');
  const [dirLoading, setDirLoading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef(null);

  useEffect(() => {
    if (!pickerOpen) return undefined;
    const onDoc = (e) => { if (pickerRef.current && !pickerRef.current.contains(e.target)) setPickerOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [pickerOpen]);

  const togglePicker = async () => {
    const next = !pickerOpen;
    setPickerOpen(next);
    if (next && !directory.length) {
      setDirLoading(true);
      try { setDirectory(await listStakeholders()); setDirError(''); }
      catch { setDirError('טעינת המאגר נכשלה.'); }
      finally { setDirLoading(false); }
    }
  };

  const addFromDirectory = (s) => {
    const primary = (s.contacts || []).find((c) => c.is_primary) || (s.contacts || [])[0] || {};
    setStakeholders((rows) => [
      ...rows,
      {
        id: `dir-${s.id}-${rows.length}`,
        stakeholderId: s.id,
        role: s.role || ROLE_OPTIONS[0],
        company: s.name || '',
        contact: primary.name || '',
        phone: s.phone || primary.phone || '',
        email: s.email || primary.email || '',
      },
    ]);
    setPickerOpen(false);
  };

  // Spec page-11 edge case: no name → fall back to an address-derived name.
  const effectiveName = () => name.trim() || [street, houseNo].filter(Boolean).join(' ').trim();

  // Persist the project and return its id (or null on validation/save failure),
  // so callers can either return to the gallery or advance to the next screen.
  async function persist(status) {
    const finalName = effectiveName();
    if (!finalName) {
      setSaveError('יש להזין שם פרויקט (או כתובת שממנה ייגזר שם).');
      return null;
    }
    setSaveError('');
    setSaving(true);
    const payload = {
      name: finalName,
      analysisEditor: editor,
      status,
      property: {
        locMode,
        city,
        street,
        houseNo,
        gush,
        helka,
        subHelka,
        addressText: locMode === 'address' ? [`${street} ${houseNo}`.trim(), city].filter(Boolean).join(', ') : '',
      },
      brief: { text: brief, tags: checkTags },
      stakeholders: stakeholders.map((s) => ({
        stakeholderId: s.stakeholderId, role: s.role, company: s.company, contact: s.contact, phone: s.phone, email: s.email,
      })),
      documents: docs.map((d) => ({ fileName: d.name, documentType: d.type, fileMeta: d.size, fileId: d.fileId })),
    };
    // Capture a Street View thumbnail for the located point (best-effort; only
    // overwrite when we actually got one, so a transient failure won't wipe it).
    if (locatedLatLng) {
      const thumb = await streetViewThumb(locatedLatLng.lat, locatedLatLng.lng);
      if (thumb) payload.thumbnail = thumb;
    }
    try {
      if (liveId) { await updateProject(liveId, payload); return liveId; }
      const created = await createProject(payload);
      if (created?.id) setLiveId(created.id);
      return created?.id || null;
    } catch (err) {
      setSaveError(err.message || 'השמירה נכשלה. נסו שוב.');
      return null;
    } finally {
      setSaving(false);
    }
  }

  // Save without leaving the screen — confirm inline so the user can keep working
  // (spec 3.1). The toast auto-dismisses; it also clears on the next edit.
  const handleSave = async (status) => {
    const wasSaved = savedOnce;
    const id = await persist(status);
    if (id) {
      setBaselineSig(currentSig());
      setSaveNotice(wasSaved ? 'השינויים נשמרו.' : 'הפרויקט נשמר. אפשר להמשיך לערוך או לעבור לשלב הבא.');
    }
  };

  useEffect(() => {
    if (!saveNotice) return undefined;
    const t = window.setTimeout(() => setSaveNotice(''), 4000);
    return () => window.clearTimeout(t);
  }, [saveNotice]);

  // "הבא" — save and continue to property identification. A new project starts
  // collecting; an existing one keeps its current status. When editing and
  // nothing changed, skip the save entirely and just advance (no-op).
  // Exit guard (spec 3.2) — if there are unsaved edits, confirm before leaving.
  const [confirmExit, setConfirmExit] = useState(false);
  const requestCancel = () => { if (isDirty()) setConfirmExit(true); else onCancel?.(); };
  const saveDraftAndExit = async () => {
    const id = await persist('draft');
    if (id) { setConfirmExit(false); onCancel?.(); }
  };

  const handleNext = async () => {
    if (savedOnce && !isDirty()) { onNext?.({ id: liveId, name: effectiveName() }); return; }
    const status = savedOnce ? (project?.project?.status || 'collecting') : 'collecting';
    const id = await persist(status);
    if (id) { setBaselineSig(currentSig()); onNext?.({ id, name: effectiveName() }); }
  };

  return (
    <div className="np">
      {/* Breadcrumb — Projects › project name › stage (matches every screen). */}
      <nav className="np-crumbs">
        <button type="button" onClick={requestCancel}>פרויקטים</button>
        <i className="fi fi-rr-angle-small-left" />
        <span className="np-crumb-name">{effectiveName() || 'פרויקט חדש'}</span>
        <i className="fi fi-rr-angle-small-left" />
        <span>פרטי פרויקט</span>
      </nav>
      <div className="np-layout">
        <aside className="np-rail">
          {/* Show the project's saved agent log here too, so it's consistent
              across steps (not empty here then suddenly full on the next step). */}
          <AgentActivity log={project?.project?.identification?.log || []} />
          <Roadmap
            activeStage={0}
            reachedStage={Math.max(reachedStageFor(project?.project), savedOnce ? 1 : 0)}
            onStageClick={(i) => {
              // Leaving the FORM forward has to save first; every other jump is
              // plain navigation to a stage the project already reached.
              if (i === 1) handleNext();
              else if (project?.project) onGoStage?.(i, { loaded: project, project: { id: project.project.id, name: effectiveName() } });
            }}
          />
          {/* Rail order is FIXED across all steps: agent log → roadmap → archive
              (identify + statutory already do this; the archive is always the
              LAST card, same slot on every screen). */}
          <DocumentArchive inline projectId={project?.project?.id} />
        </aside>
        <div className="np-main">
          <header className="np-head">
            <div className="np-head-text">
              <h1>{isEdit ? 'עריכת פרויקט' : 'פתיחת פרויקט חדש'}</h1>
              <p>
                {isEdit
                  ? 'עדכנו את פרטי הפרויקט. השינויים יישמרו עבור איסוף המידע וניתוח המצב הסטטוטורי.'
                  : 'הזינו את פרטי הפרויקט כדי להכין את המערכת לאיסוף המידע וניתוח המצב הסטטוטורי.'}
              </p>
            </div>
            <div className="np-head-actions">
              <button type="button" className="np-btn np-btn-ghost" onClick={requestCancel} disabled={saving}>ביטול</button>
              <button type="button" className="np-btn np-btn-secondary" onClick={() => handleSave('draft')} disabled={saving}>
                <i className="fi fi-rr-bookmark" /> שמירת טיוטה
              </button>
              {/* Primary = save + continue to identification (keeps status when editing). */}
              <button type="button" className="np-btn np-btn-primary" onClick={handleNext} disabled={saving}>
                {saving ? 'שומר…' : <>הבא <i className="fi fi-rr-angle-small-left" /></>}
              </button>
            </div>
          </header>

          {saveError && (
            <div className="np-saveerror" role="alert">
              <i className="fi fi-rr-exclamation" /> {saveError}
            </div>
          )}
          {saveNotice && !saveError && (
            <div className="np-savenotice" role="status" aria-live="polite">
              <i className="fi fi-rr-check-circle" /> {saveNotice}
            </div>
          )}

      <div className="np-form">
        {/* 1 — Project details */}
        <Section icon="fi-rr-edit" title="פרטי הפרויקט" helper="שם הפרויקט ועורך הניתוח.">
          <div className="np-row">
            <Field label="שם הפרויקט" required>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="הזינו שם פרויקט"
              />
            </Field>
            <Field label="עורך הניתוח" required>
              <div className="np-select-user">
                <span className="np-mini-avatar">{editor.slice(0, 1)}</span>
                <select value={editor} onChange={(e) => setEditor(e.target.value)}>
                  <option value={editor}>{editor}</option>
                </select>
                <i className="fi fi-rr-angle-small-down" />
              </div>
            </Field>
          </div>
        </Section>

        {/* 2 — Stakeholders */}
        <Section
          icon="fi-rr-users"
          title="בעלי עניין"
          helper="מזמין העבודה ובעלי עניין נוספים בפרויקט."
        >
          <div className="np-stake-toolbar">
            <div className="np-picker" ref={pickerRef}>
              <button type="button" className="np-btn-pill" onClick={togglePicker} aria-haspopup="menu" aria-expanded={pickerOpen}>
                <i className="fi fi-rr-search" /> בחר מהמאגר
                <i className="fi fi-rr-angle-small-down" />
              </button>
              {pickerOpen && (
                <div className="np-picker-pop" role="menu">
                  {dirLoading && (
                    <div className="np-picker-loading"><span className="rp-spinner" /> טוען מאגר…</div>
                  )}
                  {!dirLoading && dirError && <div className="np-picker-empty">{dirError}</div>}
                  {!dirLoading && !dirError && directory.length === 0 && (
                    <div className="np-picker-empty">המאגר ריק — הוסיפו בעלי עניין במסך «בעלי עניין».</div>
                  )}
                  {!dirLoading && directory.map((s) => (
                    <button key={s.id} type="button" role="menuitem" className="np-picker-item" onClick={() => addFromDirectory(s)}>
                      <span className="np-picker-name">{s.name}</span>
                      <span className="np-picker-sub">{[s.type, s.role].filter(Boolean).join(' · ')}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button type="button" className="np-link" onClick={openNewStakeholder}>+ הוסף חדש</button>
          </div>
          <div className="np-table" role="table">
            <div className="np-tr np-tr-head" role="row">
              <span className="np-th np-c-role">תפקיד</span>
              <span className="np-th">שם החברה / הארגון</span>
              <span className="np-th">איש קשר</span>
              <span className="np-th">טלפון</span>
              <span className="np-th">אימייל</span>
              <span className="np-th np-c-del" />
            </div>
            {stakeholders.length === 0 && (
              <div className="np-table-empty">לא נוספו בעלי עניין. לחצו «הוסף בעל עניין» כדי להוסיף.</div>
            )}
            {stakeholders.map((s) => (
              <div className="np-tr" role="row" key={s.id}>
                <span className="np-td np-c-role" data-label="תפקיד">
                  <select value={s.role} onChange={(e) => setStakeholderField(s.id, 'role', e.target.value)}>
                    {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </span>
                <span className="np-td" data-label="שם החברה / הארגון">
                  <input className="np-cell-input" value={s.company || ''} placeholder="שם החברה" onChange={(e) => setStakeholderField(s.id, 'company', e.target.value)} />
                </span>
                <span className="np-td" data-label="איש קשר">
                  <input className="np-cell-input" value={s.contact || ''} placeholder="איש קשר" onChange={(e) => setStakeholderField(s.id, 'contact', e.target.value)} />
                </span>
                <span className="np-td" data-label="טלפון">
                  <input className="np-cell-input np-ltr" value={s.phone || ''} placeholder="טלפון" onChange={(e) => setStakeholderField(s.id, 'phone', e.target.value)} />
                </span>
                <span className="np-td" data-label="אימייל">
                  <input className="np-cell-input np-ltr" value={s.email || ''} placeholder="אימייל" onChange={(e) => setStakeholderField(s.id, 'email', e.target.value)} />
                </span>
                <span className="np-td np-c-del np-c-rowactions">
                  <button type="button" aria-label="עריכת בעל עניין" title="עריכה מלאה ושמירה למאגר" onClick={() => openEditStakeholder(s)}>
                    <i className="fi fi-rr-edit" />
                  </button>
                  <button type="button" aria-label="הסר בעל עניין" onClick={() => removeStakeholder(s.id)}>
                    <i className="fi fi-rr-trash" />
                  </button>
                </span>
              </div>
            ))}
          </div>
        </Section>

        {/* 3 — Property location */}
        <Section
          icon="fi-rr-marker"
          title="איתור הנכס"
          helper="לאיתור מיקום הנכס לצורך ניתוח המצב הסטטוטורי."
        >
          <PropertyLocator
            locMode={locMode} setLocMode={setLocMode}
            city={city} setCity={setCity}
            street={street} setStreet={setStreet}
            houseNo={houseNo} setHouseNo={setHouseNo}
            gush={gush} setGush={setGush}
            helka={helka} setHelka={setHelka}
            subHelka={subHelka} setSubHelka={setSubHelka}
            onLocated={setLocatedLatLng}
          />
        </Section>

        {/* 4 — Brief / program */}
        <Section icon="fi-rr-document" title="תיאור הבדיקה / פרוגרמה" helper="תיאור מטרת הבדיקה.">
          <textarea
            className="np-textarea"
            value={brief}
            onChange={(e) => setBrief(e.target.value)}
            rows={4}
            placeholder={BRIEF_PLACEHOLDER}
          />
          {/* Check-type chips (spec 4.1) — quick-tag the analysis character. */}
          <div className="np-chips" role="group" aria-label="סוג הבדיקה">
            <span className="np-chips-hint">סמנו את מאפייני הבדיקה (לא חובה):</span>
            {CHECK_CHIP_GROUPS.map((group, gi) => (
              <div className="np-chip-row" key={gi}>
                {group.map((t) => {
                  const on = checkTags.includes(t);
                  return (
                    <button
                      type="button"
                      key={t}
                      className={`np-chip ${on ? 'is-on' : ''}`}
                      aria-pressed={on}
                      onClick={() => toggleTag(t)}
                    >
                      {on && <i className="fi fi-rr-check" />} {t}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </Section>

        {/* 5 — Documents (optional) */}
        <Section
          icon="fi-rr-folder-open"
          title="ארכיון מסמכים"
          helper="המסמכים מתווספים לאורך שלבי הניתוח. ניתן להעלות כעת או בהמשך."
        >
          <div
            className={`np-drop ${dragOver ? 'is-drag' : ''}`}
            role="button"
            tabIndex={0}
            onClick={openFilePicker}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openFilePicker(); } }}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDropFiles}
          >
            <i className="fi fi-rr-cloud-upload" />
            <span className="np-drop-title">גררו קבצים לכאן או לחצו להעלאה</span>
            <span className="np-drop-sub">PDF, DWG, DWF, JPG, PNG · עד 50MB לקובץ</span>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={DOC_ACCEPT}
              className="np-file-input"
              onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
            />
          </div>
          {docError && (
            <div className="np-doc-error" role="alert"><i className="fi fi-rr-exclamation" /> {docError}</div>
          )}
          {uploads.length > 0 && (
            <div className="np-uploads" aria-live="polite">
              {uploads.map((u) => (
                <div className={`np-upload is-${u.status}`} key={u.id}>
                  <i className={`fi ${u.status === 'done' ? 'fi-rr-check-circle' : u.status === 'error' ? 'fi-rr-exclamation' : 'fi-rr-cloud-upload'}`} />
                  <span className="np-upload-name" title={u.name}>{u.name}</span>
                  <span className="np-upload-status">
                    {u.status === 'uploading' && `מעלה קובץ… ${u.pct}%`}
                    {u.status === 'done' && 'הועלה בהצלחה'}
                    {u.status === 'error' && 'ההעלאה נכשלה — הקובץ נשמר ברשימה ללא קישור'}
                  </span>
                  {u.status === 'uploading' && (
                    <span className="np-upload-bar"><span className="np-upload-fill" style={{ width: `${u.pct}%` }} /></span>
                  )}
                  {u.status === 'error' && (
                    <button type="button" className="np-doc-x" aria-label="סגור" onClick={() => setUploads((rows) => rows.filter((x) => x.id !== u.id))}>
                      <i className="fi fi-rr-cross-small" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
          <div className="np-docs">
            {docs.map((d) => (
              <div className="np-doc" key={d.id}>
                <span className="np-doc-check"><i className="fi fi-rr-check" /></span>
                <i className="fi fi-rr-document np-doc-ic" />
                <div className="np-doc-meta">
                  <span className="np-doc-name" title={d.name}>{d.name}</span>
                  <span className="np-doc-sub">{[d.type, d.size].filter(Boolean).join(' · ')}</span>
                </div>
                <button type="button" className="np-doc-x" aria-label="הסר" onClick={() => removeDoc(d.id)}>
                  <i className="fi fi-rr-cross-small" />
                </button>
              </div>
            ))}
          </div>
        </Section>
      </div>

          {/* Footnote — actions now live in the header (top-left). */}
          <p className="np-footnote">
            <i className="fi fi-rr-info" />
            לאחר השמירה המערכת תתחיל לאסוף מידע לבניית המצב הסטטוטורי של הנכס.
          </p>
        </div>
      </div>

      {shModal && (
        <StakeholderModal
          stakeholder={shModal.stakeholder}
          seed={shModal.seed}
          onClose={() => setShModal(null)}
          onSaved={onStakeholderSaved}
        />
      )}

      {confirmExit && (
        <ConfirmDialog
          title="לצאת בלי לשמור?"
          message="יש שינויים שלא נשמרו בפרטי הפרויקט. אפשר לשמור כטיוטה, לצאת בלי לשמור, או להמשיך לערוך."
          onDismiss={() => setConfirmExit(false)}
          actions={[
            { label: 'המשך עריכה', tone: 'ghost', onClick: () => setConfirmExit(false) },
            { label: 'צא בלי לשמור', tone: 'danger', onClick: () => { setConfirmExit(false); onCancel?.(); } },
            { label: saving ? 'שומר…' : 'שמור כטיוטה', tone: 'primary', onClick: saveDraftAndExit, disabled: saving },
          ]}
        />
      )}
    </div>
  );
}
